"use client";

import type { EditorView } from "prosemirror-view";
import type { MutableRefObject, ReactNode } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuCode, LuHeading1, LuHeading2, LuHeading3 } from "react-icons/lu";
import { MdHorizontalRule } from "react-icons/md";

import DesoAvatar from "@/components/shared/deso-avatar";
import {
  EDITOR_SUGGESTION_MENU_OPEN_ATTRIBUTE,
  fitFloatingEditorSuggestionMenu,
  scheduleFitFloatingEditorSuggestionMenu,
} from "@/utils/editor-suggestion-menu";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

import {
  editorCommands,
  editorSourceCommands,
  runEditorCommand,
} from "./commands";
import { EDITOR_MODE } from "./constants";
import type { EditorSuggestionState } from "./plugins/suggestions";
import {
  closeEditorSuggestion,
  editorSuggestionsKey,
} from "./plugins/suggestions";
import { focusSchema } from "./schema";
import { useMentionSearch } from "./use-mention-search";

export type SuggestionControls = {
  moveSelection: (direction: 1 | -1) => void;
  optionCount: () => number;
  pickSelected: () => boolean;
};

type SuggestionOption = {
  key: string;
  label: string;
  onSelect: () => void;
  renderer: ReactNode;
};

const SUGGESTION_MENU_VIEWPORT_PADDING = 8;
const SUGGESTION_MENU_WIDTH = 288;

const deleteSuggestionRange = (
  view: EditorView,
  suggestion: NonNullable<EditorSuggestionState>,
) => {
  view.dispatch(
    view.state.tr.delete(suggestion.range.from, suggestion.range.to),
  );
};

const getMentionUsername = (account: PartialAccount) => {
  return getUserName({
    account,
    fallback: account.publicKey || "unknown",
  }).replace(/^[@$]/, "");
};

const insertMention = (
  view: EditorView,
  suggestion: NonNullable<EditorSuggestionState>,
  account: PartialAccount,
) => {
  const username = getMentionUsername(account);
  const mentionNode = focusSchema.nodes.mention.create({
    displayName: username,
    publicKey: account.publicKey ?? null,
    trigger: suggestion.trigger === "$" ? "$" : "@",
    username,
  });
  const insertTo = suggestion.range.from + mentionNode.nodeSize;

  view.dispatch(
    view.state.tr
      .replaceWith(suggestion.range.from, suggestion.range.to, mentionNode)
      .insertText(" ", insertTo)
      .setMeta(editorSuggestionsKey, { close: true })
      .scrollIntoView(),
  );
  view.focus();
};

const useSlashOptions = (
  mode: EDITOR_MODE,
  view: EditorView,
  suggestion: NonNullable<EditorSuggestionState>,
) =>
  useMemo(() => {
    const commandSet =
      mode === EDITOR_MODE.SIMPLE ? editorSourceCommands : editorCommands;
    const runAfterDelete = (
      command: Parameters<typeof runEditorCommand>[1],
    ) => {
      deleteSuggestionRange(view, suggestion);
      runEditorCommand(view, command);
      closeEditorSuggestion(view);
    };

    const options: SuggestionOption[] = [
      {
        key: "heading-1",
        label: "Large Heading",
        onSelect: () => runAfterDelete(commandSet.heading(1)),
        renderer: (
          <div className="flex items-center gap-2">
            <LuHeading1 />
            Large Heading
          </div>
        ),
      },
      {
        key: "heading-2",
        label: "Medium Heading",
        onSelect: () => runAfterDelete(commandSet.heading(2)),
        renderer: (
          <div className="flex items-center gap-2">
            <LuHeading2 />
            Medium Heading
          </div>
        ),
      },
      {
        key: "heading-3",
        label: "Small Heading",
        onSelect: () => runAfterDelete(commandSet.heading(3)),
        renderer: (
          <div className="flex items-center gap-2">
            <LuHeading3 />
            Small Heading
          </div>
        ),
      },
      {
        key: "code-block",
        label: "Code Block",
        onSelect: () => runAfterDelete(commandSet.codeBlock),
        renderer: (
          <div className="flex items-center gap-2">
            <LuCode />
            Code Block
          </div>
        ),
      },
      {
        key: "divider",
        label: "Divider",
        onSelect: () => runAfterDelete(commandSet.horizontalRule),
        renderer: (
          <div className="flex items-center gap-2">
            <MdHorizontalRule />
            Divider
          </div>
        ),
      },
    ];
    const query = suggestion.query.toLowerCase();

    if (!query) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [mode, suggestion, view]);

const useMentionOptions = (
  view: EditorView,
  suggestion: NonNullable<EditorSuggestionState>,
) => {
  const { accounts, loading } = useMentionSearch(
    suggestion.type === "mention" ? suggestion.query : "",
  );

  const options = useMemo(
    () =>
      accounts.map(
        (account): SuggestionOption => ({
          key:
            account.publicKey ||
            account.username ||
            getMentionUsername(account),
          label: getMentionUsername(account),
          onSelect: () => {
            insertMention(view, suggestion, account);
          },
          renderer: (
            <div className="flex min-w-0 items-center gap-2">
              <DesoAvatar account={account} size={24} clickable={false} />
              <div className="min-w-0 truncate font-semibold text-foreground">
                {suggestion.trigger === "$" ? "$" : "@"}
                {getMentionUsername(account)}
              </div>
            </div>
          ),
        }),
      ),
    [accounts, suggestion, view],
  );

  return { loading, options };
};

const useSuggestionOptions = (
  mode: EDITOR_MODE,
  view: EditorView,
  suggestion: NonNullable<EditorSuggestionState>,
) => {
  const slashOptions = useSlashOptions(mode, view, suggestion);
  const mentionOptions = useMentionOptions(view, suggestion);

  return suggestion.type === "slash"
    ? {
        loading: false,
        options: slashOptions,
      }
    : mentionOptions;
};

export const EditorSuggestionMenu = ({
  controlsRef,
  mode,
  suggestion,
  view,
}: {
  controlsRef: MutableRefObject<SuggestionControls | null>;
  mode: EDITOR_MODE;
  suggestion: NonNullable<EditorSuggestionState>;
  view: EditorView;
}) => {
  const [selected, setSelected] = useState(0);
  const { loading, options } = useSuggestionOptions(mode, view, suggestion);
  const menuRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    setSelected(0);
  }, [suggestion.query, suggestion.type]);

  useEffect(() => {
    const coords = view.coordsAtPos(suggestion.range.to);
    setPosition({
      left: Math.max(
        SUGGESTION_MENU_VIEWPORT_PADDING,
        Math.min(
          coords.left,
          window.innerWidth -
            SUGGESTION_MENU_WIDTH -
            SUGGESTION_MENU_VIEWPORT_PADDING,
        ),
      ),
      top: coords.bottom + 8,
    });
  }, [suggestion, view]);

  useLayoutEffect(() => {
    if (!menuRef.current) {
      return;
    }

    return scheduleFitFloatingEditorSuggestionMenu(
      menuRef.current,
      SUGGESTION_MENU_VIEWPORT_PADDING,
    );
  }, [loading, options.length, position.left, position.top]);

  useEffect(() => {
    const fitMenu = () => {
      if (!menuRef.current) {
        return;
      }

      fitFloatingEditorSuggestionMenu(
        menuRef.current,
        SUGGESTION_MENU_VIEWPORT_PADDING,
      );
    };

    window.addEventListener("resize", fitMenu);
    window.addEventListener("scroll", fitMenu, true);

    return () => {
      window.removeEventListener("resize", fitMenu);
      window.removeEventListener("scroll", fitMenu, true);
    };
  }, []);

  useEffect(() => {
    controlsRef.current = {
      moveSelection(direction) {
        setSelected((current) => {
          if (!options.length) {
            return 0;
          }

          return (current + direction + options.length) % options.length;
        });
      },
      optionCount: () => options.length,
      pickSelected() {
        const option = options[selected];

        if (!option) {
          return false;
        }

        option.onSelect();
        return true;
      },
    };

    return () => {
      controlsRef.current = null;
    };
  }, [controlsRef, options, selected]);

  useEffect(() => {
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest("[data-editor-v3-suggestion-menu='true']")
      ) {
        return;
      }

      closeEditorSuggestion(view);
    };

    document.addEventListener("pointerdown", closeOnOutsidePointerDown);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
    };
  }, [view]);

  if (!options.length && !loading) {
    return null;
  }

  return createPortal(
    <ul
      className="
        fixed z-200 m-0 max-h-80 w-72 max-w-[calc(100vw-16px)] list-none
        overflow-y-auto rounded-md border border-border bg-card p-1
        shadow-lg
      "
      data-editor-v3-suggestion-menu={true}
      {...{
        [EDITOR_SUGGESTION_MENU_OPEN_ATTRIBUTE]: "true",
      }}
      ref={menuRef}
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      {loading && !options.length && (
        <li className="px-3 py-2 text-sm text-muted">Searching...</li>
      )}

      {options.map((option, index) => (
        <li
          key={option.key}
          className={cn(
            `
              cursor-pointer truncate rounded-sm px-3 py-2 text-sm
              transition-colors hover:bg-accent hover:text-primary
            `,
            selected === index &&
              `
                bg-primary/12 text-primary shadow-sm ring-1
                ring-primary/25 hover:bg-primary/12 hover:text-primary
              `,
          )}
          onMouseDown={(event) => {
            event.preventDefault();
            option.onSelect();
          }}
          onMouseEnter={() => setSelected(index)}
          onMouseMove={() => setSelected(index)}
        >
          {option.renderer}
        </li>
      ))}
    </ul>,
    document.body,
  );
};
